// Enums of the notifications that travel in the contracts. `@nexxt/db` builds the Postgres enums
// `notification_channel`, `notification_status` and `suppression_kind` from these tuples.

/**
 * `in_app`: the bell (ADR-280), next to the email of a template that asks for an action. `push`: the devices of the
 * person (ADR-283), only in the preferences: a push leaves no row of its own, its row is the bell's.
 */
export const notificationChannels = ["email", "in_app", "push"] as const;
export type NotificationChannel = (typeof notificationChannels)[number];

/**
 * `queued`: written, not sent yet. `sent`: accepted by the SMTP server, not a delivery. `failed`: did not leave, the
 * row says why. `suppressed`: not sent on purpose (an address that asked for no email, a user who turned it off).
 * `read`: unused, an in-app notification is `sent` and its `read_at` says when it was read (ADR-280).
 */
export const notificationStatuses = ["queued", "sent", "failed", "suppressed", "read"] as const;
export type NotificationStatus = (typeof notificationStatuses)[number];

/** `always_allow` is in the model and not used yet (ADR-149 point 8). */
export const suppressionKinds = ["suppress", "always_allow"] as const;
export type SuppressionKind = (typeof suppressionKinds)[number];

/** Who an email is for: the users of Tomorrow, or the ones of brands, suppliers and customers. */
export const notificationAudiences = ["internal", "external"] as const;
export type NotificationAudience = (typeof notificationAudiences)[number];

/**
 * `platform.notification_follow_level` (ADR-307): what of a followed object reaches the bell. `status`, each change of
 * its status; `all`, also the changes that leave it where it is (lines, proposals, prices).
 */
export const followLevels = ["status", "all"] as const;
export type FollowLevel = (typeof followLevels)[number];

/**
 * The followable types whose domain tells the changes that leave the status where it is (`…Changed.v1`, ADR-307): only
 * these are followed with the level `all`. Orders and claims have the status alone.
 */
export const changeFollowableTypes = [
  "procurement.purchase_order",
  "procurement.inbound",
  "catalog.linesheet",
] as const;
